import type { ShippedReleaseResponse } from '@pulse/contracts';
import * as React from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { ArrowIcon, BackIcon } from '@/components/pulse/icons';
import { PageHeader } from '@/components/pulse/page-header';
import { SourceLink } from '@/components/pulse/source-link';
import { useShippedRelease } from '@/domains/shipped/api';
import { CopyButton } from '@/domains/shipped/shipped-page';

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** Shipped › a release (SHP-P8), built from the prototype's `viewRelease()`. */
export function ShippedReleasePage(): React.ReactElement {
  const { repository = '', number = '' } = useParams();
  const [search] = useSearchParams();
  const page = useShippedRelease(repository, number, search.get('date'));
  const data = page.data;
  return (
    <>
      <PageHeader
        eyebrow={`Shipped · ${data?.project.name ?? ''}`}
        title={data?.heading ?? 'Release'}
        sub={data === undefined ? '' : `${data.repositoryName} #${data.number}, merged ${data.direct ? 'straight ' : ''}into production on ${data.whenLabel}. What it ${data.direct ? 'brought' : 'carried'} to production, written for a client update.`}
        right={
          <Link className="btn" to="/shipped" style={{ textDecoration: 'none' }}>
            <BackIcon /> Shipped
          </Link>
        }
      />
      {page.isError ? <span className="sub">Pulse has no record of that release reaching production on that day.</span> : null}
      {data !== undefined ? <ReleaseView data={data} /> : null}
    </>
  );
}

export function ReleaseView({ data, initiallyOpen = false }: { readonly data: ShippedReleaseResponse; readonly initiallyOpen?: boolean }): React.ReactElement {
  const [open, setOpen] = React.useState(initiallyOpen);
  const count = data.tasks.length + data.otherFixes.length;
  return (
    <div className="two" style={{ gridTemplateColumns: 'minmax(0,1fr) 320px', alignItems: 'start' }}>
      <div className="stack" style={{ gap: 16 }}>
        <section className="card pad stack" style={{ gap: 14 }} aria-labelledby="rel-update">
          <h2 id="rel-update">The update</h2>
          <div className="stack" style={{ gap: 6 }}>
            <p style={{ margin: 0, fontSize: 16, lineHeight: 1.6, color: data.update.by === 'talos' ? 'var(--ink)' : 'var(--ink-2)' }}>{data.update.text}</p>
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>{data.update.caption}</span>
          </div>
          {data.whatsNew.length > 0 ? (
            <div className="stack" style={{ gap: 6 }}>
              <span className="eyebrow">What&apos;s new</span>
              <ul style={{ margin: 0, paddingLeft: 20, display: 'grid', gap: 6, fontSize: 15, lineHeight: 1.5 }}>
                {data.whatsNew.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          ) : null}
          <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
            <CopyButton text={data.copy} />
            {count > 0 || data.housekeeping > 0 ? (
              <button type="button" className="btn" aria-expanded={open} aria-controls="rel-tasks" onClick={() => setOpen(!open)}>
                {open ? 'Hide each task' : `See each task (${count})`}
              </button>
            ) : null}
          </div>
        </section>
        {open ? (
          <section id="rel-tasks" className="card pad stack" style={{ gap: 0 }} aria-label="Each task">
            {data.tasks.length > 0 ? (
              <span className="eyebrow" style={{ marginBottom: 4 }}>
                Stories and tasks
              </span>
            ) : null}
            {data.tasks.map((task) => (
              <SourceLink key={task.ref} className="prow" href={task.url} style={{ textDecoration: 'none', color: 'var(--ink)', gridTemplateColumns: '96px minmax(0,1fr) auto', padding: '9px 0' }}>
                <span className="mono" style={{ fontSize: 12, color: 'var(--accent-ink)' }}>
                  {task.ref}
                </span>
                <span>{task.title}</span>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>{plural(task.prs, 'PR', 'PRs')}</span>
              </SourceLink>
            ))}
            {data.otherFixes.length > 0 ? (
              <div className="prow" style={{ gridTemplateColumns: '96px minmax(0,1fr)', padding: '9px 0' }}>
                <span style={{ fontSize: 12, color: 'var(--muted)' }}>Other fixes</span>
                <span style={{ color: 'var(--ink-2)' }}>
                  {data.otherFixes.map((fix, index) => (
                    <React.Fragment key={fix.url}>
                      {index > 0 ? '; ' : ''}
                      <SourceLink href={fix.url} style={{ color: 'inherit' }}>
                        {fix.text}
                      </SourceLink>
                    </React.Fragment>
                  ))}
                </span>
              </div>
            ) : null}
            {data.housekeeping > 0 ? (
              <div className="prow" style={{ gridTemplateColumns: '96px minmax(0,1fr)', padding: '9px 0', opacity: 0.72 }}>
                <span className="chip c-kind" style={{ justifySelf: 'start' }}>
                  Housekeeping
                </span>
                <span style={{ color: 'var(--muted)' }}>{plural(data.housekeeping, 'PR', 'PRs')}: dependency bumps, branch syncs and the like</span>
              </div>
            ) : null}
          </section>
        ) : null}
      </div>
      <aside className="stack" style={{ gap: 16 }}>
        <section className="card pad stack" style={{ gap: 8 }}>
          <h2>{data.direct ? 'This PR' : 'This release'}</h2>
          <span style={{ color: 'var(--ink-2)' }}>{data.title}</span>
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>
            {data.direct
              ? 'Merged straight into production, not through the dev branch.'
              : `${plural(data.prs, 'PR', 'PRs')} from the dev branch: ${plural(data.tasks.length, 'story or task', 'stories and tasks')}${data.otherFixes.length > 0 ? `, ${plural(data.otherFixes.length, 'other fix', 'other fixes')}` : ''}${data.housekeeping > 0 ? `, ${data.housekeeping} housekeeping` : ''}.`}
          </span>
          <SourceLink className="linkish" href={data.url}>
            Open {data.repositoryName} #{data.number} <ArrowIcon />
          </SourceLink>
        </section>
        <section className="card pad stack" style={{ gap: 8 }}>
          <h2>How this reads</h2>
          <span style={{ color: 'var(--ink-2)' }}>
            A release merges the dev branch into production, so it carries every PR merged to dev since the last one. The update lists what users get, in plain words; each task shows where it came from.
          </span>
        </section>
      </aside>
    </div>
  );
}
